import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imageUrl, formatRupiah, formatDiscount, errorMessage } from "../lib/api";
import { useSettings } from "../context/SettingsContext";
import { useCart } from "../context/CartContext";
import { getStatus, STATUS_BADGE_CLASSES } from "../lib/status";
import { ArrowLeft, Package, Share2, Tag, Ruler, Layers, Sparkles, BadgePercent, ShoppingCart, X, Plus, Minus } from "lucide-react";
import { toast } from "sonner";
import WhatsAppFab from "../components/WhatsAppFab";

export default function ProductDetail() {
    const { slug } = useParams();
    const { settings } = useSettings();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showAddDialog, setShowAddDialog] = useState(false);
    const openAddToCart = () => setShowAddDialog(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/products/slug/${slug}`);
                if (!cancelled) setProduct(data);
            } catch (e) {
                if (!cancelled) setError(errorMessage(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const share = async () => {
        const url = window.location.href;
        const title = product?.name || "Produk";
        try {
            if (navigator.share) {
                await navigator.share({ title, url });
            } else {
                await navigator.clipboard.writeText(url);
                alert("Link disalin");
            }
        } catch (e) {
            console.warn("Share failed:", e);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background">
                <div className="aspect-square w-full animate-pulse bg-secondary" />
                <div className="space-y-3 p-5">
                    <div className="h-6 w-2/3 animate-pulse rounded bg-secondary" />
                    <div className="h-4 w-1/2 animate-pulse rounded bg-secondary" />
                    <div className="h-20 w-full animate-pulse rounded bg-secondary" />
                </div>
            </div>
        );
    }

    if (error || !product) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
                <Package size={36} className="text-muted-foreground" strokeWidth={1.25} />
                <h2 className="mt-4 font-heading text-xl font-semibold">Produk tidak ditemukan</h2>
                <p className="mt-1 text-sm text-muted-foreground">{error || "Mungkin sudah dihapus atau link salah."}</p>
                <Link
                    to="/"
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground"
                    data-testid="back-to-catalog-link"
                >
                    <ArrowLeft size={14} /> Ke Katalog
                </Link>
            </div>
        );
    }

    const hasDiscount = product.list_price && product.list_price > product.price && product.discount_percent > 0;
    const status = getStatus(product.status);
    const statusClass = STATUS_BADGE_CLASSES[status.tone];
    const canOrder = !["habis", "discontinued"].includes(product.status);

    return (
        <div data-testid="product-detail-page" className="min-h-screen bg-background pb-28">
            {/* Floating top bar */}
            <div className="sticky top-0 z-30 flex items-center justify-between bg-background/70 px-4 py-3 backdrop-blur-xl">
                <Link
                    to="/"
                    data-testid="product-back-link"
                    className="inline-flex h-10 items-center justify-center gap-2 rounded-full border border-border bg-white/80 px-3 text-sm text-foreground transition-all hover:bg-secondary"
                    aria-label="Kembali"
                >
                    <ArrowLeft size={16} strokeWidth={1.75} />
                    <span className="font-heading text-xs font-semibold tracking-wide truncate max-w-[40vw]" data-testid="store-badge">
                        {settings.name}
                    </span>
                </Link>
                <button
                    onClick={share}
                    data-testid="product-share-btn"
                    className="inline-flex h-10 items-center justify-center gap-1.5 rounded-full border border-border bg-white/80 px-4 text-sm text-foreground transition-all hover:bg-secondary"
                >
                    <Share2 size={16} strokeWidth={1.75} /> Bagikan
                </button>
            </div>

            {/* Hero image — show full product without aggressive cropping */}
            <div className="fade-up relative -mt-14 w-full bg-[#FDFBF7]" data-testid="product-image-section">
                <div className="mx-auto aspect-square w-full max-w-[680px] overflow-hidden">
                    {product.image_path ? (
                        <img
                            src={imageUrl(product.image_path)}
                            alt={product.name}
                            className="h-full w-full object-contain"
                            loading="eager"
                            fetchpriority="high"
                        />
                    ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                            <Package size={56} strokeWidth={1.25} />
                        </div>
                    )}
                </div>
                {hasDiscount && (
                    <span className="absolute right-4 top-20 rounded-full bg-accent px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent-foreground shadow-sm">
                        -{formatDiscount(product.discount_percent)}% OFF
                    </span>
                )}
            </div>

            {/* Content */}
            <div className="fade-up mx-auto max-w-2xl px-5 pt-6 sm:pt-8" style={{ animationDelay: "120ms" }}>
                {product.category && (
                    <span className="label-eyebrow text-accent" data-testid="product-category">
                        {product.category}
                    </span>
                )}
                <h1
                    data-testid="product-name"
                    className="mt-2 font-heading text-3xl font-semibold leading-tight tracking-tight sm:text-4xl text-balance"
                >
                    {product.name}
                </h1>

                {/* Price block */}
                <div className="mt-6 flex flex-wrap items-baseline gap-3" data-testid="product-pricing">
                    <span
                        data-testid="product-price"
                        className="font-heading text-3xl font-semibold text-primary sm:text-4xl"
                    >
                        {product.variants?.length > 0 && (
                            <span className="text-base font-normal text-muted-foreground mr-1.5">mulai dari</span>
                        )}
                        {formatRupiah(product.price)}
                    </span>
                    {hasDiscount && (
                        <>
                            <span
                                data-testid="product-list-price"
                                className="text-base text-muted-foreground line-through"
                            >
                                {formatRupiah(product.list_price)}
                            </span>
                            <span
                                data-testid="product-discount-badge"
                                className="inline-flex items-center gap-1 rounded-full bg-accent/15 px-2.5 py-1 text-xs font-semibold text-accent"
                            >
                                <BadgePercent size={12} strokeWidth={2} />
                                Hemat {formatDiscount(product.discount_percent)}%
                            </span>
                        </>
                    )}
                    <span
                        data-testid="product-status-badge"
                        className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wider ${statusClass}`}
                    >
                        {status.label}
                    </span>
                </div>

                {/* Variants table */}
                {product.variants?.length > 0 && (
                    <div data-testid="product-variants" className="mt-6 rounded-lg border border-border overflow-hidden bg-white">
                        <div className="grid grid-cols-[1fr_1fr_auto] gap-3 px-4 py-2.5 bg-[hsl(var(--muted))] text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            <span>Varian</span>
                            <span>Sub Varian</span>
                            <span className="text-right">Harga</span>
                        </div>
                        {product.variants.map((v, i) => {
                            const vHasDisc = (v.list_price || 0) > (v.price || 0) && (v.discount_percent || 0) > 0;
                            return (
                                <div
                                    key={i}
                                    data-testid={`variant-row-${i}`}
                                    className="grid grid-cols-[1fr_1fr_auto] gap-3 px-4 py-3 border-t border-border text-sm items-center"
                                >
                                    <span className="font-medium">{v.varian || "—"}</span>
                                    <span className="text-muted-foreground">{v.sub_varian || "—"}</span>
                                    <span className="text-right">
                                        {vHasDisc && (
                                            <span className="block text-[11px] text-muted-foreground line-through leading-none">{formatRupiah(v.list_price)}</span>
                                        )}
                                        <span className="font-semibold text-primary">{formatRupiah(v.price)}</span>
                                        {vHasDisc && (
                                            <span className="ml-1.5 inline-flex items-center rounded-full bg-accent/15 px-1.5 py-0.5 text-[9px] font-semibold text-accent uppercase">
                                                -{formatDiscount(v.discount_percent)}%
                                            </span>
                                        )}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Meta */}
                <dl className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {product.sku && (
                        <MetaItem icon={<Tag size={16} strokeWidth={1.6} />} label="SKU" value={product.sku} testId="meta-sku" />
                    )}
                    {product.sizes?.length > 0 && (
                        <MetaItem
                            icon={<Ruler size={16} strokeWidth={1.6} />}
                            label="Ukuran"
                            value={product.sizes.join(" · ")}
                            testId="meta-sizes"
                        />
                    )}
                    {product.category && (
                        <MetaItem
                            icon={<Layers size={16} strokeWidth={1.6} />}
                            label="Kategori"
                            value={product.category}
                            testId="meta-category"
                        />
                    )}
                </dl>

                {/* Description */}
                {product.description && (
                    <div className="mt-8 rounded-lg border border-border bg-white p-5">
                        <div className="flex items-center gap-2 text-foreground">
                            <Sparkles size={16} strokeWidth={1.6} className="text-accent" />
                            <span className="label-eyebrow">Deskripsi</span>
                        </div>
                        <p
                            data-testid="product-description"
                            className="mt-3 whitespace-pre-line text-base leading-relaxed text-foreground/85"
                        >
                            {product.description}
                        </p>
                    </div>
                )}

                {/* Sizes pill list */}
                {product.sizes?.length > 0 && (
                    <div className="mt-6">
                        <span className="label-eyebrow text-muted-foreground">Pilih Ukuran</span>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="product-sizes-list">
                            {product.sizes.map((s) => (
                                <span
                                    key={s}
                                    className="rounded-full border border-border bg-white px-4 py-1.5 text-sm font-medium hover:bg-secondary transition-colors duration-200 cursor-pointer"
                                >
                                    {s}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Sticky CTA */}
            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/85 backdrop-blur-xl">
                <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
                    <div className="flex flex-col">
                        <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Harga</span>
                        <span className="font-heading text-lg font-semibold text-foreground">{formatRupiah(product.price)}</span>
                    </div>
                    <button
                        onClick={share}
                        data-testid="product-share-cta"
                        title="Bagikan"
                        className="ml-auto inline-flex h-12 w-12 items-center justify-center rounded-full border border-border bg-white text-foreground transition-all duration-200 hover:bg-secondary active:scale-[0.98]"
                    >
                        <Share2 size={18} strokeWidth={1.75} />
                    </button>
                    <button
                        onClick={openAddToCart}
                        data-testid="add-to-cart-cta"
                        disabled={!canOrder}
                        className="hidden inline-flex h-12 items-center gap-2 rounded-full bg-accent px-5 text-sm font-medium text-accent-foreground transition-all duration-200 hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ShoppingCart size={16} strokeWidth={2} /> Tambah ke Keranjang
                    </button>
                </div>
            </div>
            <WhatsAppFab raised message={`Halo ${settings?.name || "Toko"}, saya tertarik dengan produk "${product.name}". Apakah masih tersedia?`} />
            {showAddDialog && (
                <AddToCartDialog
                    product={product}
                    onClose={() => setShowAddDialog(false)}
                />
            )}
        </div>
    );
}

function AddToCartDialog({ product, onClose }) {
    const { addItem } = useCart();
    const variants = product.variants && product.variants.length > 0
        ? product.variants
        : [{ varian: "", sub_varian: "", price: product.price }];
    // qtyMap keyed by index
    const [qtyMap, setQtyMap] = useState({});

    const setQty = (i, v) => setQtyMap((m) => ({ ...m, [i]: Math.max(0, Number(v) || 0) }));
    const inc = (i) => setQtyMap((m) => ({ ...m, [i]: Math.max(0, (m[i] || 0) + 1) }));
    const dec = (i) => setQtyMap((m) => ({ ...m, [i]: Math.max(0, (m[i] || 0) - 1) }));

    const totalQty = Object.values(qtyMap).reduce((a, b) => a + (b || 0), 0);
    const totalPrice = variants.reduce((sum, v, i) => sum + (v.price || product.price) * (qtyMap[i] || 0), 0);

    const submit = () => {
        let any = false;
        variants.forEach((v, i) => {
            const q = qtyMap[i] || 0;
            if (q > 0) {
                addItem({
                    product_id: product.id,
                    name: product.name,
                    slug: product.slug,
                    image_path: product.image_path,
                    sku: product.sku,
                    status: product.status,
                    varian: v.varian || "",
                    sub_varian: v.sub_varian || "",
                    unit_price: v.price || product.price,
                    quantity: q,
                });
                any = true;
            }
        });
        if (any) {
            toast.success(`${totalQty} item ditambahkan ke keranjang`);
            onClose();
        }
    };

    return (
        <div data-testid="add-to-cart-dialog" className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-foreground/50 backdrop-blur-sm p-0 sm:p-4" onClick={onClose}>
            <div className="w-full max-w-lg max-h-[85vh] overflow-hidden bg-white sm:rounded-xl rounded-t-2xl shadow-xl flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between border-b border-border px-5 py-4 shrink-0">
                    <div>
                        <h3 className="font-heading text-lg font-semibold">Pilih Varian & Jumlah</h3>
                        <p className="text-xs text-muted-foreground line-clamp-1">{product.name}</p>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="add-cart-close">
                        <X size={20} />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
                    {variants.map((v, i) => {
                        const q = qtyMap[i] || 0;
                        const label = [v.varian, v.sub_varian].filter(Boolean).join(" · ") || "Standar";
                        return (
                            <div key={i} data-testid={`variant-pick-${i}`} className="flex items-center gap-3 rounded-lg border border-border p-3">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium truncate">{label}</div>
                                    <div className="text-xs text-primary font-semibold">{formatRupiah(v.price || product.price)}</div>
                                </div>
                                <div className="inline-flex items-center gap-1">
                                    <button data-testid={`qty-dec-${i}`} onClick={() => dec(i)} className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-secondary">
                                        <Minus size={14} />
                                    </button>
                                    <input
                                        data-testid={`qty-input-${i}`}
                                        type="number"
                                        min="0"
                                        value={q}
                                        onChange={(e) => setQty(i, e.target.value)}
                                        className="w-12 text-center text-sm rounded-md border border-border py-1 outline-none focus:border-accent"
                                    />
                                    <button data-testid={`qty-inc-${i}`} onClick={() => inc(i)} className="grid h-8 w-8 place-items-center rounded-full border border-border hover:bg-secondary">
                                        <Plus size={14} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="border-t border-border px-5 py-4 flex items-center justify-between gap-3 shrink-0">
                    <div className="text-sm">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Total</div>
                        <div className="font-heading font-semibold text-foreground" data-testid="add-cart-total">
                            {totalQty} item · {formatRupiah(totalPrice)}
                        </div>
                    </div>
                    <button
                        data-testid="add-cart-submit"
                        onClick={submit}
                        disabled={totalQty === 0}
                        className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        <ShoppingCart size={14} /> Tambah
                    </button>
                </div>
            </div>
        </div>
    );
}

function MetaItem({ icon, label, value, testId }) {
    return (
        <div data-testid={testId} className="flex items-start gap-3 rounded-md border border-border bg-white px-4 py-3">
            <div className="mt-0.5 text-accent">{icon}</div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
                <div className="mt-0.5 truncate text-sm font-medium text-foreground">{value}</div>
            </div>
        </div>
    );
}
